/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { ISchemaCommentsModel, ValidatorString } from "@adapptio/lib-blueprint";
import { useFieldState } from "../../../ui/hooks/useFieldGroup";
import { FieldGroup, FieldText, Form } from "@adapptio/lib-editor-ui";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";

export interface IInspectorControlCommentsAddProps {
	model: ISchemaCommentsModel;
	onAdd: (value: string) => void;
}

export const InspectorControlCommentsAdd: React.FC<IInspectorControlCommentsAddProps> = ({
	model,
	onAdd
}) => {
	const baseClass = BEM("edt-inspector-control-comments-add");

	const [ fieldState, setFieldState ] = useFieldState(
		{
			message: {
				defaultValue: "",
				validators: [ ValidatorString({ required: true, min: 1 }) ]
			}
		},
		[ model ]
	);

	const handleAdd = useCallback(() => {
		onAdd(fieldState.message.value);
		setFieldState.message("");
	}, [ onAdd, fieldState.message.value ]);

	return (
		<div className={baseClass.toClassName()}>
			<Form onSubmit={handleAdd}>
				<div>
					<FieldGroup grow>
						<FieldText
							grow
							value={fieldState.message.value}
							onChange={setFieldState.message}
							multiLine
							maxRows={3}
							placeholder="Add comment..."
						/>
					</FieldGroup>
				</div>

				<div style={{ textAlign: "right" }}>
					<ActionButton
						className={baseClass.element("submit-btn").toClassName()}
						onClick={handleAdd}
						label="Add Comment"
						type="submit"
						icon="mdi/comment-plus"
						size={ACTION_BUTTON_SIZE.SMALL}
						style={ACTION_BUTTON_STYLE.PRIMARY}
						disabled={!fieldState.message.value}
					/>
				</div>
			</Form>
		</div>
	);
};
